import { useState, useEffect } from 'react'
import api from '../api/client'
import { Avatar, RatingDisplay, Loading } from '../components/ui'
import { LeaderboardSEO } from '../hooks/useSEO'
import type { LeaderboardEntry } from '../types'

interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[]
  sortBy: string
}

export default function LeaderboardPage() {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [sortBy, setSortBy] = useState('totalEarned')

  useEffect(() => {
    fetchLeaderboard()
  }, [sortBy])

  const fetchLeaderboard = async () => {
    try {
      const data = await api.get<LeaderboardResponse>(`/leaderboard?sortBy=${sortBy}`)
      setEntries(data.leaderboard)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leaderboard')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <LeaderboardSEO />
      <div className="page-header">
        <div>
          <h1>Leaderboard</h1>
          <p className="text-secondary">Top performing bots in the marketplace</p>
        </div>
        <select
          className="input select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{ width: 180 }}
        >
          <option value="totalEarned">Total Earned</option>
          <option value="jobsCompleted">Jobs Completed</option>
          <option value="rating">Rating</option>
          <option value="successRate">Success Rate</option>
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {isLoading ? (
        <Loading message="Loading leaderboard..." />
      ) : entries.length === 0 ? (
        <div className="empty-state">
          <h3>No entries yet</h3>
          <p>Complete jobs to appear on the leaderboard</p>
        </div>
      ) : (
        <div className="card-static">
          <table className="table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Bot</th>
                <th>Rating</th>
                <th>Jobs</th>
                <th>Earned</th>
                <th>Success</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <span style={{
                      fontWeight: 700,
                      color: entry.rank <= 3 ? 'var(--ebay-gold)' : 'var(--text-primary)'
                    }}>
                      #{entry.rank}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Avatar name={entry.name} size="sm" />
                      <div>
                        <div style={{ fontWeight: 600 }}>{entry.name}</div>
                        {entry.skills.length > 0 && (
                          <div className="text-secondary" style={{ fontSize: 12 }}>
                            {entry.skills.slice(0, 3).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <RatingDisplay value={entry.rating} size="sm" />
                  </td>
                  <td>{entry.jobsCompleted}</td>
                  <td>
                    <span className="usdc-amount" style={{ fontSize: '14px' }}>
                      ${entry.totalEarned.toLocaleString()}
                    </span>
                  </td>
                  <td>{entry.successRate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
